import { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Chip,
} from "@mui/material";
import { KeyboardArrowDown, Download, Delete, Add } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import {
  EvaluationResponse,
  SingleEvaluationResponse,
} from "../../Models/evaluation";
import {
  EvaluationStatus,
  Features,
  UserRole,
} from "../../Enums/companyStatus";
import colors from "../../Constants/colors";
import { getGradeText } from "../../Helpers/gradeHelpers";
import { getLanguageTranslationKey } from "../../Helpers/stringHelpers";
import { hasPermission, hasRole } from "../../Helpers/authHelpers";
import { useNavigate } from "react-router-dom";
import { ComoButton } from "../Common/ComoButton";

export interface EvaluationSelectorProps {
  evaluations: EvaluationResponse[];
  selectedEvaluation?: SingleEvaluationResponse;
  onSelectEvaluation: (evaluationId: number) => void;
  onDownloadEvaluation?: (evaluationId: number) => void;
  onDeleteEvaluation?: (evaluationId: number) => void;
}

export function EvaluationSelector({
  evaluations,
  selectedEvaluation,
  onSelectEvaluation,
  onDownloadEvaluation,
  onDeleteEvaluation,
}: EvaluationSelectorProps) {
  const { t } = useTranslation("singleCompanyComponent");
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  const handleSelect = (evaluation: EvaluationResponse) => {
    onSelectEvaluation(evaluation.evaluation_id);
    setIsOpen(false);
  };

  return (
    <Box sx={{ mb: 3, display: "flex", alignItems: "center" }}>
      <Box
        sx={{
          position: "relative",
          maxWidth: 600,
          flexShrink: 0,
          width: "100%",
        }}
      >
        <Button
          onClick={() => setIsOpen(!isOpen)}
          fullWidth
          sx={{
            py: 1.25,
            px: 2,
            bgcolor: "white",
            border: `1px solid ${colors.gray300}`,
            borderRadius: 2,
            justifyContent: "space-between",
            textTransform: "none",
            color: colors.gray900,
            "&:hover": { bgcolor: colors.gray50 },
            "&:focus": {
              outline: "none",
              borderColor: colors.primary_dark,
              boxShadow: `0 0 0 2px ${colors.accent}40`,
            },
          }}
          endIcon={
            <KeyboardArrowDown
              sx={{
                transform: isOpen ? "rotate(180deg)" : "none",
                transition: "transform 0.2s",
              }}
            />
          }
        >
          {selectedEvaluation
            ? `${selectedEvaluation.question_list_name} ${t(getLanguageTranslationKey(selectedEvaluation.language))}`
            : t("evaluationSelector.selectPlaceholder")}
        </Button>

        {isOpen && (
          <Paper
            elevation={3}
            sx={{
              position: "absolute",
              zIndex: 10,
              width: "100%",
              mt: 0.5,
              maxHeight: 240,
              overflow: "auto",
              borderRadius: 2,
              border: `1px solid ${colors.gray300}`,
            }}
          >
            {evaluations.map((evaluation) => {
              const isPaused = evaluation?.status === EvaluationStatus.PAUSED;
              return (
                <Box
                  key={evaluation.evaluation_id}
                  onClick={() => handleSelect(evaluation)}
                  sx={{
                    px: 2,
                    py: 1.5,
                    cursor: "pointer",
                    bgcolor:
                      selectedEvaluation?.id === evaluation.evaluation_id
                        ? `${colors.primary_dark}0F`
                        : "white",
                    borderLeft:
                      selectedEvaluation?.id === evaluation.evaluation_id
                        ? `4px solid ${colors.accent}`
                        : "4px solid transparent",
                    "&:hover": {
                      bgcolor: `${colors.primary_dark}0F`,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: colors.gray900,
                        }}
                      >
                        {evaluation.question_list_name}{" "}
                        {t(getLanguageTranslationKey(evaluation.language))}
                        {!evaluation.is_up_to_date && (
                          <Chip
                            label={t("evaluationSelector.outdated")}
                            sx={{
                              ml: 1,
                              fontSize: "0.6rem",
                              height: 16,
                              backgroundColor: colors.primary_dark,
                              color: colors.white,
                              "& .MuiChip-label": { px: 0.75 },
                            }}
                          />
                        )}
                        {isPaused && (
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{ color: colors.gray500, ml: 1 }}
                          >
                            {t("evaluationSelector.stopped")}
                          </Typography>
                        )}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: colors.gray600 }}
                      >
                        {t(getGradeText(evaluation.grade))}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {!isPaused &&
                        (hasPermission(Features.QA_INSIGHTS) ||
                          hasRole(UserRole.ADMIN)) && (
                          <IconButton
                            size="small"
                            onClick={() =>
                              onDownloadEvaluation?.(evaluation.evaluation_id)
                            }
                            sx={{ "&:hover": { bgcolor: colors.blue50 } }}
                          >
                            <Download
                              sx={{
                                color: colors.accent,
                                fontSize: "1.2rem",
                              }}
                            />
                          </IconButton>
                        )}
                      <IconButton
                        size="small"
                        onClick={() =>
                          onDeleteEvaluation?.(evaluation.evaluation_id)
                        }
                        sx={{ "&:hover": { bgcolor: colors.red50 } }}
                      >
                        <Delete
                          sx={{
                            color: colors.red600,
                            fontSize: "1.2rem",
                          }}
                        />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Paper>
        )}
      </Box>
      <ComoButton
        startIcon={<Add />}
        sx={{
          whiteSpace: "nowrap",
          ml: "auto",
        }}
        onClick={() => {
          if (selectedEvaluation?.company_id) {
            navigate("/evaluation", {
              state: { companyId: selectedEvaluation.company_id },
            });
          }
        }}
        disabled={!selectedEvaluation?.company_id}
      >
        {t("evaluationSelector.startNewEvaluation")}
      </ComoButton>
    </Box>
  );
}
